/**
 * Supabase client configuration
 * Environment variables: REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY
 */

import { createClient } from '@supabase/supabase-js';


export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY';

// Create and export the Supabase client with explicit auth options for session persistence
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
        persistSession: true,
        autoRefreshToken: true, // Auto-refresh tokens before they expire
        detectSessionInUrl: true,
        storageKey: 'coq-magic-auth',
        storage: window.localStorage
    }
});

// supabaseAnon is no longer needed since we use Edge Functions

const IS_DEV = process.env.NODE_ENV !== 'production';

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
    return SUPABASE_URL !== 'YOUR_SUPABASE_URL' &&
        SUPABASE_ANON_KEY !== 'YOUR_SUPABASE_ANON_KEY' &&
        SUPABASE_URL.includes('supabase.co');
};

/**
 * Enhanced Moroccan phone number validation and formatting
 * @param {string} phone - Raw phone number
 * @returns {Object} { isValid: boolean, formatted: string, error: string }
 */
function validateAndFormatMoroccanPhone(phone) {
    if (!phone || typeof phone !== 'string') {
        return { isValid: false, formatted: '', error: 'Numéro de téléphone requis' };
    }

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Check length - Moroccan mobile numbers are 9 digits after country code
    if (digits.length < 9) {
        return { isValid: false, formatted: '', error: 'Numéro trop court (minimum 9 chiffres)' };
    }

    if (digits.length > 12) {
        return { isValid: false, formatted: '', error: 'Numéro trop long (maximum 12 chiffres)' };
    }

    // Extract last 9 digits (the actual Moroccan mobile number)
    const mobileNumber = digits.slice(-9);

    // Check if it starts with valid Moroccan mobile prefix
    const validPrefixes = ['6', '7']; // Moroccan mobile prefixes (includes 7 for some operators)
    if (!validPrefixes.includes(mobileNumber.charAt(0))) {
        return { isValid: false, formatted: '', error: 'Doit commencer par 6 ou 7 (mobile marocain)' };
    }

    // Format as +212 XXX XXX XXX
    const formatted = `+212${mobileNumber.slice(0, 3)} ${mobileNumber.slice(3, 6)} ${mobileNumber.slice(6)}`;

    return { isValid: true, formatted, error: null };
}

const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

// Rate limiting is now handled by Edge Function

/**
 * Create a new order
 * @param {Object} orderData - Order details
 * @returns {Object} Created order or error
 */
export async function createOrder(orderData) {
    try {
        // Auth check is now handled by Edge Function

        // Prepare payload for Edge Function
        const customerPhone = String(orderData?.customerPhone || '').trim();

        // Ensure items have necessary fields for the Edge Function
        const rawItems = Array.isArray(orderData?.items) ? orderData.items : [];
        const items = rawItems
            .slice(0, 50)
            .map((it) => ({
                id: it?.id,
                title: String(it?.title || '').slice(0, 120),
                price: Number(it?.price ?? 0), // Edge Function will verify this, but we pass it for type safety
                qty: Math.max(1, Math.min(99, Number(it?.qty ?? 1)))
            }))
            .filter((it) => it.id != null && it.qty > 0);

        if (items.length === 0) {
            return { success: false, error: 'Panier vide' };
        }

        const payload = {
            customer_name: String(orderData?.customerName || '').trim().slice(0, 80),
            customer_phone: customerPhone,
            delivery_address: String(orderData?.deliveryAddress || '').trim().slice(0, 240),
            delivery_zone: orderData?.deliveryZone || null,
            items: items,
            notes: String(orderData?.notes || '').trim().slice(0, 600)
        };

        // Call the Edge Function
        // functions.invoke automatically handles the Authorization header if a session exists
        const { data, error } = await supabase.functions.invoke('validate-order', {
            body: payload
        });

        if (error) {
            console.error('Edge Function Error:', error);
            // Attempt to parse a more user-friendly error from the response body if available
            // Note: supabase-js might wrap the error, so we check for context
            let errorMessage = 'Erreur lors de la commande';

            // If the function returned a structured error response that Supabase caught
            if (error instanceof Error) {
                errorMessage = error.message;
            }

            return { success: false, error: errorMessage };
        }

        if (!data?.success) {
            return { success: false, error: data?.error || 'Erreur inconnue' };
        }

        // Return compliance with local expectation
        // We might need to enrich the returned order with local data if the UI expects it immediately
        // The Edge Function returns: { success: true, order: { id, order_number, total, status } }

        // We can merge with our initial payload to give the UI a "complete" looking object 
        // without a secondary fetch, or just return what the DB gave us. 
        // The UI likely needs 'items' and 'customer_name' to show the "Success" screen details.
        const returnedOrder = {
            ...data.order,
            items: items, // Pass back the items we sent
            customer_name: payload.customer_name,
            delivery_address: payload.delivery_address,
            created_at: new Date().toISOString()
        };

        return { success: true, order: returnedOrder };

    } catch (error) {
        console.error('Error creating order:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get all orders (for admin dashboard)
 * @param {string} status - Filter by status (optional)
 * @returns {Array} List of orders
 */
export async function getOrders(status = null) {
    try {
        // FORCE REFRESH SESSION: Ensure we have a valid token before asking for data
        // This fixes the "intermittent 401/empty data" on refresh
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError || !user) {
            if (IS_DEV) console.warn('getOrders: No active session found via getUser()', authError);
            return { success: false, error: 'No active session' };
        }

        let query = supabase
            .from('orders')
            .select('*')
            .order('created_at', { ascending: false });

        if (status) {
            query = query.eq('status', status);
        }

        // Execute query
        const { data, error } = await query;




        if (error) throw error;
        return { success: true, orders: data };
    } catch (error) {
        console.error('Error fetching orders:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Update order status
 * @param {string} orderId - Order ID
 * @param {string} newStatus - New status
 * @returns {Object} Updated order
 */
export async function updateOrderStatus(orderId, newStatus) {
    try {
        const updates = { status: newStatus };

        // Add timestamps for specific statuses
        if (newStatus === 'confirmed') {
            updates.confirmed_at = new Date().toISOString();
        } else if (newStatus === 'delivered') {
            updates.delivered_at = new Date().toISOString();
        }

        const { data, error } = await supabase
            .from('orders')
            .update(updates)
            .eq('id', orderId)
            .select();

        if (error) throw error;

        // Check if any rows were updated
        if (!data || data.length === 0) {
            return { success: false, error: 'Order not found or access denied by RLS policy' };
        }

        return { success: true, order: data[0] };
    } catch (error) {
        console.error('Error updating order:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get order by order number
 * @param {string} orderNumber - Order number (e.g., COQ-20260127-ABCD)
 * @returns {Object} Order details
 */
export async function getOrderByNumber(orderNumber) {
    try {
        const { data, error } = await supabase
            .from('orders')
            .select('*')
            .eq('order_number', orderNumber)
            .single();

        if (error) throw error;
        return { success: true, order: data };
    } catch (error) {
        console.error('Error fetching order:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get orders by customer phone (for account page - user's order history)
 * @param {string} phone - Customer phone (raw or formatted)
 * @returns {Object} List of orders
 */
export async function getOrdersByCustomerPhone(phone) {
    try {
        if (!phone || String(phone).replace(/\D/g, '').length < 9) {
            return { success: true, orders: [] };
        }
        const formatted = formatMoroccanPhone(phone);
        const digits = formatted.replace(/\D/g, '').slice(-9); // Last 9 digits for alternative formats

        const { data, error } = await supabase
            .from('orders')
            .select('*')
            .or(`customer_phone.eq.${formatted},customer_phone.eq.+212${digits},customer_phone.eq.0${digits},customer_phone.eq.${digits}`)
            .order('created_at', { ascending: false })
            .limit(20);

        if (error) throw error;
        return { success: true, orders: data || [] };
    } catch (error) {
        console.error('Error fetching orders by phone:', error);
        return { success: false, error: error.message, orders: [] };
    }
}

/**
 * Get orders by user_id (for account page when user is logged in)
 * @param {string} userId - Auth user UUID
 * @returns {Object} List of orders
 */
export async function getOrdersByUserId(userId) {
    try {
        if (!userId) return { success: true, orders: [] };
        const { data, error } = await supabase
            .from('orders')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(20);
        if (error) throw error;
        return { success: true, orders: data || [] };
    } catch (error) {
        console.error('Error fetching orders by user_id:', error);
        return { success: false, error: error.message, orders: [] };
    }
}



/**
 * Get or create customer by phone
 * @param {string} phone - Customer phone number
 * @param {string} name - Customer name (optional)
 * @returns {Object} Customer record
 */
export async function getOrCreateCustomer(phone, name = null) {
    try {
        const normalizedPhone = normalizePhone(phone);
        if (!normalizedPhone) {
            return { success: false, error: 'Numéro invalide' };
        }
        // First try to find existing customer
        let { data: customer, error } = await supabase
            .from('customers')
            .select('*')
            .eq('phone', normalizedPhone)
            .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
            throw error;
        }

        // If customer doesn't exist, create new one
        if (!customer) {
            const { data: newCustomer, error: createError } = await supabase
                .from('customers')
                .insert([{ phone: normalizedPhone, name }])
                .select()
                .single();

            if (createError) throw createError;
            customer = newCustomer;
        }

        return { success: true, customer };
    } catch (error) {
        console.error('Error with customer:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Check if customer is blacklisted
 * @param {string} phone - Customer phone number
 * @returns {boolean} True if blacklisted
 */
export async function isCustomerBlacklisted(phone) {
    try {
        const normalizedPhone = normalizePhone(phone);
        if (!normalizedPhone) return false;
        const { data, error } = await supabase
            .from('customers')
            .select('is_blacklisted')
            .eq('phone', normalizedPhone)
            .single();

        if (error && error.code !== 'PGRST116') throw error;
        return data?.is_blacklisted || false;
    } catch (error) {
        console.error('Error checking blacklist:', error);
        return false;
    }
}

/**
 * Blacklist a customer
 * @param {string} phone - Customer phone number
 * @param {boolean} blacklist - True to blacklist, false to unblacklist
 */
export async function setCustomerBlacklist(phone, blacklist = true, name = null) {
    try {
        const normalizedPhone = normalizePhone(phone);
        if (!normalizedPhone) {
            return { success: false, error: 'Numéro invalide' };
        }

        const payload = { phone: normalizedPhone, is_blacklisted: blacklist };
        if (name) payload.name = name;

        const { data, error } = await supabase
            .from('customers')
            .upsert([payload], { onConflict: 'phone' })
            .select();

        if (error) throw error;
        return { success: true, customer: data?.[0] };
    } catch (error) {
        console.error('Error updating blacklist:', error);
        return { success: false, error: error.message };
    }
}



/**
 * Get all active delivery zones
 * @returns {Array} List of delivery zones
 */
export async function getDeliveryZones() {
    try {
        const { data, error } = await supabase
            .from('delivery_zones')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) throw error;
        return { success: true, zones: data };
    } catch (error) {
        console.error('Error fetching zones:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get delivery fee for a zone
 * @param {string} zoneId - Zone ID
 * @returns {Object} Delivery fee and minimum order
 */
export async function getZoneDetails(zoneId) {
    try {
        const { data, error } = await supabase
            .from('delivery_zones')
            .select('*')
            .eq('id', zoneId)
            .single();

        if (error) throw error;
        return { success: true, zone: data };
    } catch (error) {
        console.error('Error fetching zone:', error);
        return { success: false, error: error.message };
    }
}



/**
 * Subscribe to new orders (for admin dashboard)
 * @param {Function} callback - Function to call when new order arrives
 * @returns {Object} Subscription object (call .unsubscribe() to stop)
 */
export function subscribeToOrders(callback) {
    return supabase
        .channel('orders')
        .on('postgres_changes',
            { event: '*', schema: 'public', table: 'orders' },
            (payload) => {
                // Pass event type and data to callback
                callback({
                    eventType: payload.eventType, // 'INSERT', 'UPDATE', or 'DELETE'
                    new: payload.new,
                    old: payload.old
                });
            }
        )
        .subscribe();
}

/**
 * Format phone number for Morocco (+212) - Enhanced version
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number
 */
export function formatMoroccanPhone(phone) {
    const validation = validateAndFormatMoroccanPhone(phone);
    return validation.isValid ? validation.formatted : phone; // Return original if invalid
}



/**
 * Get all users from user_profiles
 * @returns {Array} List of users with their profiles
 */
export async function getUsers() {
    try {
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return { success: true, users: data || [] };
    } catch (error) {
        console.error('Error fetching users:', error);
        return { success: false, error: error.message, users: [] };
    }
}

/**
 * Update user role
 * @param {string} userId - User ID
 * @param {string} newRole - New role (admin, manager, equipier, client)
 */
export async function updateUserRole(userId, newRole) {
    try {
        const { data, error } = await supabase
            .from('user_profiles')
            .update({ role: newRole })
            .eq('id', userId)
            .select()
            .single();

        if (error) throw error;
        return { success: true, user: data };
    } catch (error) {
        console.error('Error updating user role:', error);
        return { success: false, error: error.message };
    }
}



/**
 * Get all menu items
 * @returns {Array} List of menu items
 */
export async function getMenuItems() {
    try {
        const { data, error } = await supabase
            .from('menu_items')
            .select('*')
            .order('category')
            .order('title');

        if (error) throw error;
        return { success: true, items: data || [] };
    } catch (error) {
        console.error('Error fetching menu items:', error);
        return { success: false, error: error.message, items: [] };
    }
}

/**
 * Create a new menu item
 * @param {Object} item - Menu item data
 */
export async function createMenuItem(item) {
    try {
        const { data, error } = await supabase
            .from('menu_items')
            .insert([{
                title: item.title,
                description: item.description || '',
                price: item.price,
                category: item.category,
                image_url: item.image_url || '',
                is_featured: item.is_featured || false
            }])
            .select()
            .single();

        if (error) throw error;
        return { success: true, item: data };
    } catch (error) {
        console.error('Error creating menu item:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Update a menu item
 * @param {string} itemId - Menu item ID
 * @param {Object} updates - Fields to update
 */
export async function updateMenuItem(itemId, updates) {
    try {
        const { data, error } = await supabase
            .from('menu_items')
            .update(updates)
            .eq('id', itemId)
            .select()
            .single();

        if (error) throw error;
        return { success: true, item: data };
    } catch (error) {
        console.error('Error updating menu item:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Delete a menu item
 * @param {string} itemId - Menu item ID
 */
export async function deleteMenuItem(itemId) {
    try {
        const { error } = await supabase
            .from('menu_items')
            .delete()
            .eq('id', itemId);

        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error deleting menu item:', error);
        return { success: false, error: error.message };
    }
}

export default supabase;



/**
 * Create a new reservation
 * @param {Object} reservation - Reservation details
 */
export async function createReservation(reservation) {
    try {
        const { data, error } = await supabase
            .from('reservations')
            .insert([{
                name: reservation.name,
                phone: reservation.phone,
                date: reservation.date,
                time: reservation.time,
                guests: reservation.guests,
                notes: reservation.notes || '',
                status: 'pending'
            }])
            .select()
            .single();

        if (error) throw error;
        return { success: true, reservation: data };
    } catch (error) {
        console.error('Error creating reservation:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get all reservations
 * @returns {Array} List of reservations
 */
export async function getReservations() {
    try {
        const { data, error } = await supabase
            .from('reservations')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return { success: true, reservations: data || [] };
    } catch (error) {
        console.error('Error fetching reservations:', error);
        return { success: false, error: error.message, reservations: [] };
    }
}

/**
 * Update reservation status
 * @param {string} id - Reservation ID
 * @param {string} status - New status (confirmed/cancelled/pending)
 */
export async function updateReservationStatus(id, status) {
    try {
        const { data, error } = await supabase
            .from('reservations')
            .update({ status })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return { success: true, reservation: data };
    } catch (error) {
        console.error('Error updating reservation status:', error);
        return { success: false, error: error.message };
    }
}



/**
 * Submit contact form with reCAPTCHA v3 token (calls Edge Function that verifies then inserts)
 * Use this when REACT_APP_RECAPTCHA_SITE_KEY is set to prevent spam.
 */
export async function submitContactWithRecaptcha(token, msg, accessToken = null) {
    try {
        let tokenToUse = accessToken;
        if (!tokenToUse) {
            const { data } = await supabase.auth.getSession();
            tokenToUse = data?.session?.access_token || null;
        }

        const url = `${SUPABASE_URL}/functions/v1/contact-with-recaptcha`;
        const headers = { 'Content-Type': 'application/json' };
        if (tokenToUse) {
            headers['Authorization'] = `Bearer ${tokenToUse}`;
        }

        const res = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                token,
                name: msg.name,
                contact: msg.contact,
                msg: msg.msg || msg.message
            })
        });
        const data = await res.json();
        if (!res.ok) return { success: false, error: data.error || 'Erreur serveur' };
        return { success: true, message: data.message };
    } catch (error) {
        console.error('Error submitting contact with reCAPTCHA:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Create a new contact message (direct insert - use when reCAPTCHA Edge Function not used)
 * @param {Object} msg - Message details
 */
export async function createContactMessage(msg) {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user) {
            return { success: false, error: 'Veuillez vous connecter' };
        }

        const name = String(msg?.name || '').trim();
        const contact = String(msg?.contact || '').trim();
        const message = String(msg?.msg || msg?.message || '').trim();
        if (!name || !contact || !message) {
            return { success: false, error: 'Champs requis manquants' };
        }
        if (name.length > 80 || contact.length > 120 || message.length > 1200) {
            return { success: false, error: 'Message trop long' };
        }

        const { data, error } = await supabase
            .from('contact_messages')
            .insert([{
                name,
                contact,
                message,
                is_read: false
            }])
            .select()
            .single();

        if (error) throw error;
        return { success: true, message: data };
    } catch (error) {
        console.error('Error creating message:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get all contact messages
 * @returns {Array} List of messages
 */
export async function getContactMessages() {
    try {
        const { data, error } = await supabase
            .from('contact_messages')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return { success: true, messages: data || [] };
    } catch (error) {
        console.error('Error fetching messages:', error);
        return { success: false, error: error.message, messages: [] };
    }
}

/**
 * Mark message as read
 * @param {string} id - Message ID
 */
export async function markMessageRead(id) {
    try {
        const { data, error } = await supabase
            .from('contact_messages')
            .update({ is_read: true })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return { success: true, message: data };
    } catch (error) {
        console.error('Error marking message as read:', error);
        return { success: false, error: error.message };
    }
}


